/**
 * Collision-aware "safe zones" for floating UI elements.
 *
 * Any component that occupies space at the bottom of the viewport (sticky add
 * to cart, cookie banner, promo bar, mobile bottom nav, etc.) registers its
 * height via `registerBottomSafeZone()`. The chat widget (and any other
 * floating element) reads the current max via the CSS variable
 * `--bottom-safe-offset` and offsets its `bottom` accordingly with a smooth
 * transition.
 */

const _claims = new Map(); // id -> height (px)

function _recompute() {
    const max = Array.from(_claims.values()).reduce((acc, h) => (h > acc ? h : acc), 0);
    document.documentElement.style.setProperty("--bottom-safe-offset", `${max}px`);
}

let _seed = 0;
const _newId = () => `safe-zone-${++_seed}`;

export function registerBottomSafeZone(initialHeight = 0) {
    const id = _newId();
    _claims.set(id, initialHeight);
    _recompute();
    return {
        update(height) {
            _claims.set(id, Math.max(0, Math.round(height || 0)));
            _recompute();
        },
        release() {
            _claims.delete(id);
            _recompute();
        },
    };
}

/* React hook: pass a ref to your sticky bottom element + an `active` flag.
   Returns nothing; the CSS variable is updated automatically. */
import { useEffect } from "react";

export function useBottomSafeZone(elementRef, active = true) {
    useEffect(() => {
        if (!active || !elementRef?.current) return undefined;
        const handle = registerBottomSafeZone(0);
        // Observe size changes (orientation, dynamic content, etc.)
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                handle.update(entry.contentRect.height + 36); // +36px ensures >= 24px gap incl. chat button radius
            }
        });
        ro.observe(elementRef.current);
        return () => {
            ro.disconnect();
            handle.release();
        };
    }, [elementRef, active]);
}
